// The in-call screen. Connects a LiveKit Room with the token minted by the backend,
// publishes mic (and camera on a video call), and renders a tile per participant.
// Self-contained: the parent gives it a session and an onHangup, nothing else.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import MicOffRoundedIcon from "@mui/icons-material/MicOffRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import VideocamOffRoundedIcon from "@mui/icons-material/VideocamOffRounded";
import CallEndRoundedIcon from "@mui/icons-material/CallEndRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import { Room, RoomEvent, Track, type Participant } from "livekit-client";

import type { CallSession } from "../../../types/call";
import { initials, avatarColor } from "../../../pages/chat/chatHelpers";
import { muteCallParticipant, kickCallParticipant } from "../../../redux/apis/callApis";
import { getChatSocket } from "../../../config/chatSocket";
import { showErrorToast } from "../../../utils/toastUtils";
import { Avatar as MuiAvatar, List, ListItem } from "@mui/material";
import AddToCallDialog from "./AddToCallDialog";

interface Props {
  session: CallSession;
  // True until a remote participant is actually in the room (caller is "ringing").
  ringing: boolean;
  // Only the caller may pull a third person in — shows the "add" button.
  isInitiator: boolean;
  // Who we're calling / who called — for the ringing header.
  peerName: string;
  onHangup: () => void;
}

// One participant's media. Attaches their camera + mic tracks to elements and
// re-attaches whenever the room signals a track change (via the `rev` prop).
const ParticipantTile: React.FC<{ participant: Participant; local: boolean; rev: number }> = ({
  participant,
  local,
  rev,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    const cam = participant.getTrackPublication(Track.Source.Camera);
    const camTrack = cam?.videoTrack;
    if (camTrack && !cam?.isMuted && videoRef.current) {
      camTrack.attach(videoRef.current);
      setHasVideo(true);
    } else {
      setHasVideo(false);
    }
    // Don't play our own mic back — that's an echo. Remote audio only.
    if (!local && audioRef.current) {
      const mic = participant.getTrackPublication(Track.Source.Microphone);
      if (mic?.audioTrack) mic.audioTrack.attach(audioRef.current);
    }
    return () => {
      camTrack?.detach();
    };
  }, [participant, local, rev]);

  const name = participant.name || participant.identity;
  return (
    <Box
      sx={{
        position: "relative",
        flex: "1 1 300px",
        minWidth: 220,
        aspectRatio: "4 / 3",
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "#11161c",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        playsInline
        muted={local}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: hasVideo ? "block" : "none",
          transform: local ? "scaleX(-1)" : "none", // mirror own camera
        }}
      />
      {!hasVideo && (
        <Avatar sx={{ width: 84, height: 84, fontSize: 30, bgcolor: avatarColor(name) }}>
          {initials(name) || <PersonRoundedIcon />}
        </Avatar>
      )}
      {!local && <audio ref={audioRef} autoPlay />}
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          left: 10,
          bottom: 8,
          px: 1,
          py: 0.25,
          borderRadius: 1.5,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.45)",
          fontWeight: 600,
        }}
      >
        {name}
        {local ? " (You)" : ""}
      </Typography>
    </Box>
  );
};

const CallOverlay: React.FC<Props> = ({ session, ringing, isInitiator, peerName, onHangup }) => {
  const roomRef = useRef<Room | null>(null);
  const [participants, setParticipants] = useState<Array<Participant>>([]);
  const [rev, setRev] = useState(0); // bump to re-attach tiles on any track change
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(session.call.callType === "video");
  const [elapsed, setElapsed] = useState(0);
  // Shrink the call to a floating widget so the app stays usable during a call.
  const [minimized, setMinimized] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const bump = useCallback(() => setRev((r) => r + 1), []);

  // Connect once.
  useEffect(() => {
    let cancelled = false;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    const refresh = () => {
      if (cancelled) return;
      setParticipants([room.localParticipant, ...Array.from(room.remoteParticipants.values())]);
      bump();
    };

    room
      .on(RoomEvent.ParticipantConnected, refresh)
      .on(RoomEvent.ParticipantDisconnected, refresh)
      .on(RoomEvent.TrackSubscribed, refresh)
      .on(RoomEvent.TrackUnsubscribed, refresh)
      .on(RoomEvent.TrackMuted, bump)
      .on(RoomEvent.TrackUnmuted, bump)
      .on(RoomEvent.LocalTrackPublished, refresh)
      .on(RoomEvent.Disconnected, () => {
        if (!cancelled) onHangup();
      });

    (async () => {
      try {
        await room.connect(session.url, session.token);
        if (cancelled) return;
        await room.localParticipant.setMicrophoneEnabled(true);
        if (session.call.callType === "video") {
          await room.localParticipant.setCameraEnabled(true);
        }
        refresh();
      } catch (e) {
        console.error("[CALL] connect failed:", e);
        onHangup();
      }
    })();

    return () => {
      cancelled = true;
      room.disconnect();
      roomRef.current = null;
    };
    // Connect exactly once per session (keyed on roomName). onHangup/session fields
    // are intentionally not deps — re-running would tear down a live call.
  }, [session.roomName]); // eslint-disable-line

  // The caller can mute me: apply it to my own mic and reflect it in the button.
  useEffect(() => {
    const socket = getChatSocket();
    const onMute = async (evt: { callId: number; muted: boolean }) => {
      if (evt.callId !== session.call.id) return;
      const room = roomRef.current;
      if (!room) return;
      try {
        await room.localParticipant.setMicrophoneEnabled(!evt.muted);
        setMicOn(!evt.muted);
      } catch {
        /* ignore */
      }
    };
    socket.on("call:mute", onMute);
    return () => {
      socket.off("call:mute", onMute);
    };
  }, [session.call.id]);

  // Call timer starts once someone else is here.
  const remoteCount = participants.filter((p) => !(p as any).isLocal).length;
  useEffect(() => {
    if (remoteCount === 0) return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [remoteCount]);

  const toggleMic = async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micOn;
    await room.localParticipant.setMicrophoneEnabled(next);
    setMicOn(next);
  };
  const toggleCam = async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !camOn;
    await room.localParticipant.setCameraEnabled(next);
    setCamOn(next);
    bump();
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const showRinging = ringing && remoteCount === 0;

  const statusLine = showRinging ? "Ringing…" : remoteCount === 0 ? "Connecting…" : mmss;

  // Initiator moderation.
  const muteOther = async (p: Participant) => {
    const micPub = p.getTrackPublication(Track.Source.Microphone);
    const isMuted = micPub?.isMuted ?? false;
    try {
      await muteCallParticipant(session.call.id, p.identity, !isMuted);
    } catch {
      showErrorToast("Could not change mute");
    }
  };
  const kickOther = async (p: Participant) => {
    try {
      await kickCallParticipant(session.call.id, p.identity);
    } catch {
      showErrorToast("Could not remove participant");
    }
  };

  const localIdentity = roomRef.current?.localParticipant.identity;

  return (
    <Box
      sx={
        minimized
          ? {
              position: "fixed",
              bottom: 20,
              right: 20,
              width: 260,
              zIndex: 2000,
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "rgba(9,12,16,0.98)",
              boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
              display: "flex",
              flexDirection: "column",
              color: "#fff",
            }
          : {
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              bgcolor: "rgba(9,12,16,0.97)",
              display: "flex",
              flexDirection: "column",
              color: "#fff",
            }
      }
    >
      {minimized ? (
        // ---- Compact floating widget ----
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
              {peerName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {statusLine}
            </Typography>
          </Box>
          <IconButton size="small" onClick={toggleMic} sx={{ color: "#fff" }}>
            {micOn ? <MicRoundedIcon fontSize="small" /> : <MicOffRoundedIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={onHangup} sx={{ bgcolor: "#e5484d", color: "#fff", "&:hover": { bgcolor: "#c93b40" } }}>
            <CallEndRoundedIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Expand">
            <IconButton size="small" onClick={() => setMinimized(false)} sx={{ color: "#fff" }}>
              <OpenInFullRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        // ---- Full header (with a minimise button) ----
        <Box sx={{ position: "relative" }}>
          <Tooltip title="Minimise">
            <IconButton
              onClick={() => setMinimized(true)}
              sx={{ position: "absolute", right: 12, top: 12, color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" } }}
            >
              <CloseFullscreenRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Stack alignItems="center" sx={{ pt: 3, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {peerName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {statusLine}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Tiles — kept mounted even when minimised so audio never drops; just hidden. */}
      <Box
        sx={{
          flex: minimized ? "none" : 1,
          display: minimized ? "none" : "flex",
          flexWrap: "wrap",
          gap: 2,
          alignContent: "center",
          justifyContent: "center",
          p: 2,
          overflow: "auto",
        }}
      >
        {participants.map((p) => (
          <ParticipantTile
            key={p.identity}
            participant={p}
            local={(p as any).isLocal === true || p.identity === roomRef.current?.localParticipant.identity}
            rev={rev}
          />
        ))}
      </Box>

      {/* Full controls — hidden in the minimised widget (it has its own). */}
      {!minimized && (
      <Stack direction="row" justifyContent="center" spacing={2} sx={{ pb: 4, pt: 1 }}>
        <Tooltip title={micOn ? "Mute" : "Unmute"}>
          <IconButton
            onClick={toggleMic}
            sx={{
              width: 56,
              height: 56,
              color: "#fff",
              bgcolor: micOn ? "rgba(255,255,255,0.14)" : "#fff",
              "& svg": { color: micOn ? "#fff" : "#11161c" },
              "&:hover": { bgcolor: micOn ? "rgba(255,255,255,0.24)" : "#eee" },
            }}
          >
            {micOn ? <MicRoundedIcon /> : <MicOffRoundedIcon />}
          </IconButton>
        </Tooltip>

        {session.call.callType === "video" && (
          <Tooltip title={camOn ? "Turn camera off" : "Turn camera on"}>
            <IconButton
              onClick={toggleCam}
              sx={{
                width: 56,
                height: 56,
                color: "#fff",
                bgcolor: camOn ? "rgba(255,255,255,0.14)" : "#fff",
                "& svg": { color: camOn ? "#fff" : "#11161c" },
                "&:hover": { bgcolor: camOn ? "rgba(255,255,255,0.24)" : "#eee" },
              }}
            >
              {camOn ? <VideocamRoundedIcon /> : <VideocamOffRoundedIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* Participants list (everyone can view; the caller gets moderation). */}
        <Tooltip title="Participants">
          <IconButton
            onClick={() => setPeopleOpen((v) => !v)}
            sx={{
              width: 56,
              height: 56,
              color: "#fff",
              bgcolor: peopleOpen ? "#fff" : "rgba(255,255,255,0.14)",
              "& svg": { color: peopleOpen ? "#11161c" : "#fff" },
              "&:hover": { bgcolor: peopleOpen ? "#eee" : "rgba(255,255,255,0.24)" },
            }}
          >
            <PeopleAltRoundedIcon />
          </IconButton>
        </Tooltip>

        {/* Only the caller can pull in a third person. */}
        {isInitiator && (
          <Tooltip title="Add person">
            <IconButton
              onClick={() => setAddOpen(true)}
              sx={{
                width: 56,
                height: 56,
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.14)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
              }}
            >
              <PersonAddRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Hang up">
          <IconButton
            onClick={onHangup}
            sx={{ width: 56, height: 56, bgcolor: "#e5484d", color: "#fff", "&:hover": { bgcolor: "#c93b40" } }}
          >
            <CallEndRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      )}

      {/* Participants panel — a slide-in list. The caller can mute/remove others. */}
      {peopleOpen && !minimized && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: "80%", sm: 320 },
            bgcolor: "#141a21",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            flexDirection: "column",
            zIndex: 5,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Participants ({participants.length})</Typography>
            <IconButton size="small" onClick={() => setPeopleOpen(false)} sx={{ color: "#fff" }}>
              <CloseFullscreenRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <List sx={{ flex: 1, overflowY: "auto", px: 1 }}>
            {participants.map((p) => {
              const isLocal = p.identity === localIdentity;
              const micPub = p.getTrackPublication(Track.Source.Microphone);
              const isMuted = micPub?.isMuted ?? false;
              return (
                <ListItem key={p.identity} sx={{ px: 1, py: 0.75, gap: 1 }}>
                  <MuiAvatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: avatarColor(p.name || p.identity) }}>
                    {initials(p.name || p.identity)}
                  </MuiAvatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {p.name || p.identity}
                      {isLocal ? " (You)" : ""}
                    </Typography>
                    {isMuted && (
                      <Typography variant="caption" sx={{ color: "#e5484d" }}>
                        Muted
                      </Typography>
                    )}
                  </Box>
                  {/* Caller-only controls, and never on themselves. */}
                  {isInitiator && !isLocal && (
                    <>
                      <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                        <IconButton size="small" onClick={() => muteOther(p)} sx={{ color: "#fff" }}>
                          {isMuted ? <MicOffRoundedIcon fontSize="small" /> : <MicRoundedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove from call">
                        <IconButton size="small" onClick={() => kickOther(p)} sx={{ color: "#e5484d" }}>
                          <PersonRemoveRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {isInitiator && (
        <AddToCallDialog open={addOpen} callId={session.call.id} onClose={() => setAddOpen(false)} />
      )}
    </Box>
  );
};

export default CallOverlay;
